/**
 * «Bitácora», one record opened (the owner, 2026-10-12: «on the activity log for each item can we
 * have it be clickable and have it show all relevant details of that particular log event? is that
 * possible?»).
 *
 * A record is a type and a payload of ids and whole numbers: centavos, thousandths of a unit, a
 * rate times ten thousand, a uuid for every product, client and till. This says one the way a
 * person reads it: everything it recorded under its own name, an id as the name it stands for, an
 * amount in its currency, a quantity in its unit; the record a void or a payment is of, which
 * opens; what came after it about the same thing, which opens too; and what a change changed, with
 * what it said before. The sheet (`ui/DetalleEvento.tsx`) adds who did it, when, on which device,
 * and how the books took it.
 *
 * `describirRegistro` is pure, so the sheet and the tests share one answer. `cargarDetalle` reads
 * what it needs from this device's copy of the log, as everything in «Bitácora» does: it works
 * with no signal.
 */

import {
  FRECUENCIAS_PAGO,
  ZONA_HORARIA,
  camposDelEvento,
  costOf,
  esCuentaDeBanco,
  esCuentaEnDolares,
  formatFxRate,
  formatMoney,
  formatQuantity,
  idDeCamion,
  type CampoDeEvento,
  type Centavos,
  type FxRate,
  type Milli,
  type Role,
  type SettingKey,
  type Settings,
  type UnitCost,
} from '@abastece/shared';
import { ACCOUNTS, type CategoriaGasto } from '@abastece/core';
import { db, ordenReplay, type AsientoRow, type LocalEvent } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { localeActual, traducir as t, type Clave } from '../i18n/index.js';
import { etiquetaCampo, etiquetaValor } from '../i18n/campos.js';
import {
  etiquetaCategoriaGasto,
  etiquetaCategoriaProducto,
  etiquetaCuenta,
  etiquetaEventoIdioma,
  etiquetaLugar,
  etiquetaRol,
  etiquetaUnidad,
} from '../i18n/dominio.js';
import { nombreProducto } from '../i18n/productos.js';
import { nombreDeCuenta } from './bancos.js';
import { dichoAcordado } from './planilla.js';
import { NOMBRE_AJUSTE, verMargen } from './configuracion.js';
import { leerSinEfecto } from './sinEfecto.js';

// ---------------------------------------------------------------------------------------------
// What each field holds
// ---------------------------------------------------------------------------------------------

type Tipo =
  | 'oculto'
  | 'texto'
  | 'numero'
  | 'siNo'
  | 'eleccion'
  | 'cordobas'
  | 'dolares'
  | 'enSuMoneda'
  | 'costoUnitario'
  | 'tasa'
  | 'cantidad'
  | 'factor'
  | 'dias'
  | 'tarifa'
  | 'fecha'
  | 'mes'
  | 'instante'
  | 'diaSemana'
  | 'forma'
  | 'lugar'
  | 'cuenta'
  | 'rol'
  | 'categoria'
  | 'unidad'
  | 'moneda'
  | 'producto'
  | 'cliente'
  | 'proveedor'
  | 'usuario'
  | 'empleado'
  | 'equipo'
  | 'listaPrecio'
  | 'presentacion'
  | 'registro'
  | 'evento'
  | 'indice'
  | 'codigos'
  | 'foto'
  | 'gastoFijo'
  | 'objeto'
  | 'lineas'
  | 'ajuste';

/**
 * Every field of the registry, by what it holds. Money is centavos of córdobas unless it says
 * otherwise (`dolares`, or `enSuMoneda`: the purchase's, the expense's, the bank account's);
 * quantities are thousandths, in the unit of the product or of the presentation on the same line.
 * The ids of what a record creates are its own and say nothing; the ids of anything else are the
 * name they stand for, or the record they point at.
 */
const TIPO_DE_CAMPO: Record<string, Tipo> = {
  // The record's own id, when it is nothing but that.
  adelantoId: 'oculto',
  vacacionesId: 'oculto',
  compraSaldoId: 'oculto',
  conteoId: 'oculto',
  devolucionId: 'oculto',
  reempaqueId: 'oculto',
  salidaId: 'oculto',
  trasladoId: 'oculto',
  id: 'oculto',
  // What nobody should read off a screen, or the books' own bookkeeping.
  pinHash: 'oculto',
  costoReferencia: 'oculto',
  sinContar: 'oculto',
  reemplazoDespues: 'oculto',
  donde: 'oculto',
  anterior: 'oculto',

  // Another record: what it was, and it opens.
  abonoId: 'registro',
  compraId: 'registro',
  depositoId: 'registro',
  gastoId: 'registro',
  incobrableId: 'registro',
  jornadaId: 'registro',
  movimientoId: 'registro',
  ofertaId: 'registro',
  pagoId: 'registro',
  pedidoId: 'registro',
  planillaId: 'registro',
  recargaId: 'registro',
  relevoDe: 'registro',
  reemplazo: 'registro',
  sesionId: 'registro',
  transferenciaId: 'registro',
  ventaId: 'registro',
  eventoId: 'evento',
  corteId: 'evento',
  aperturaSiguienteId: 'evento',
  indice: 'indice',

  // Somebody or something with a name.
  productoId: 'producto',
  origenProductoId: 'producto',
  clienteId: 'cliente',
  proveedorId: 'proveedor',
  usuarioId: 'usuario',
  autorizadoPor: 'usuario',
  apartadoAutorizadoPor: 'usuario',
  faltaAutorizadaPor: 'usuario',
  listaAutorizadaPor: 'usuario',
  recibidoPor: 'usuario',
  repartidorId: 'usuario',
  empleadoId: 'empleado',
  dispositivoId: 'equipo',
  listaId: 'listaPrecio',
  unidadVentaId: 'presentacion',

  // Córdobas.
  adelantoMaximo: 'cordobas',
  aguinaldoPagado: 'cordobas',
  bruto: 'cordobas',
  cambio: 'cordobas',
  conteoCordobas: 'cordobas',
  conteoEfectivo: 'cordobas',
  contadoCordobas: 'cordobas',
  cordobas: 'cordobas',
  costo: 'cordobas',
  costoAdicional: 'cordobas',
  deGeneral: 'cordobas',
  descuento: 'cordobas',
  envio: 'cordobas',
  esperado: 'cordobas',
  facial: 'cordobas',
  fondoInicial: 'cordobas',
  inatec: 'cordobas',
  inss: 'cordobas',
  ir: 'cordobas',
  limiteCredito: 'cordobas',
  montoFacial: 'cordobas',
  otrosGravables: 'cordobas',
  precio: 'cordobas',
  precioLista: 'cordobas',
  precioRegular: 'cordobas',
  precioUnitario: 'cordobas',
  primerConteo: 'cordobas',
  primerConteoCordobas: 'cordobas',
  queda: 'cordobas',
  redondeo: 'cordobas',
  salarioMensual: 'cordobas',
  tarifa: 'tarifa',
  salarioPendiente: 'cordobas',
  saldo: 'cordobas',
  subtotal: 'cordobas',
  total: 'cordobas',
  vacacionesPagadas: 'cordobas',
  // Dollars.
  conteoDolares: 'dolares',
  contadoDolares: 'dolares',
  dolares: 'dolares',
  fondoDolares: 'dolares',
  montoOriginal: 'dolares',
  primerConteoDolares: 'dolares',
  saldoDolares: 'dolares',
  // In the currency of the purchase, the expense, the bank account.
  costoTotal: 'enSuMoneda',
  flete: 'enSuMoneda',
  iva: 'enSuMoneda',
  monto: 'enSuMoneda',
  recibido: 'enSuMoneda',
  costoUnitario: 'costoUnitario',
  tasaCambio: 'tasa',

  // Quantities.
  cantidad: 'cantidad',
  cantidadBase: 'cantidad',
  cantidadContada: 'cantidad',
  cantidadSistema: 'cantidad',
  mermaCantidad: 'cantidad',
  origenCantidad: 'cantidad',
  consumido: 'cantidad',
  stockMinimo: 'cantidad',
  factor: 'factor',
  diasVacaciones: 'dias',
  dias: 'dias',

  // Days and times.
  fecha: 'fecha',
  fechaEntrega: 'fecha',
  fechaIngreso: 'fecha',
  vencePago: 'fecha',
  vencimiento: 'fecha',
  desde: 'fecha',
  hasta: 'fecha',
  periodo: 'mes',
  contadoEn: 'instante',
  diaVisita: 'diaSemana',

  // One of a few.
  forma: 'forma',
  formaPago: 'forma',
  reembolso: 'forma',
  liquidacion: 'forma',
  origen: 'forma',
  destino: 'forma',
  ubicacion: 'lugar',
  ubicacionRetorno: 'lugar',
  camion: 'lugar',
  cuenta: 'cuenta',
  cuentaDolares: 'cuenta',
  hacia: 'cuenta',
  rol: 'rol',
  categoria: 'categoria',
  unidadBase: 'unidad',
  moneda: 'moneda',
  tipo: 'eleccion',
  estado: 'eleccion',
  frecuencia: 'eleccion',

  // Yes or no.
  aFiado: 'siNo',
  conInss: 'siNo',
  conPrestaciones: 'siNo',
  esMedicamento: 'siNo',
  esPredeterminada: 'siNo',
  inicial: 'siNo',
  perecedero: 'siNo',
  porDia: 'siNo',
  reingresar: 'siNo',
  reingresarStock: 'siNo',
  respaldo: 'siNo',
  soloSiHay: 'siNo',

  // Words, as typed.
  cargo: 'texto',
  cedula: 'texto',
  codigo: 'texto',
  contacto: 'texto',
  descripcion: 'texto',
  direccion: 'texto',
  documento: 'texto',
  inssNumero: 'texto',
  loteId: 'texto',
  motivo: 'texto',
  motivoOverride: 'texto',
  nombre: 'texto',
  nombreEn: 'texto',
  nota: 'texto',
  notas: 'texto',
  numero: 'texto',
  operadora: 'texto',
  referencia: 'texto',
  registroSanitario: 'texto',
  telefono: 'texto',
  ticket: 'texto',
  orden: 'numero',
  // So many for fewer (2026-11-03): how many are taken, and paid.
  lleva: 'numero',
  paga: 'numero',

  // Things.
  codigosBarras: 'codigos',
  foto: 'foto',
  fotoCedula: 'foto',
  gastoFijo: 'gastoFijo',
  lote: 'objeto',
  unidadVenta: 'objeto',
  lineas: 'lineas',
  pagos: 'lineas',
  precios: 'lineas',
  destinos: 'lineas',
  conteoCamion: 'lineas',
  clave: 'ajuste',
  valor: 'ajuste',
};

/** Where one name holds another thing in one kind of record. */
const TIPO_EN_EL_REGISTRO: Record<string, Record<string, Tipo>> = {
  'banco.transferido': { desde: 'cuenta' },
  'inventario.traslado': { origen: 'lugar', destino: 'lugar' },
  'inventario.salida': { motivo: 'eleccion' },
  'empleado.liquidado': { motivo: 'eleccion' },
  'oferta.creada': { motivo: 'eleccion' },
  // The payment it changes, by its own id: `planilla.pagada` has none of its own (2026-10-31).
  'planilla.corregida': { pagoId: 'evento' },
};

function tipoDe(tipoEvento: string, campo: string): Tipo {
  return TIPO_EN_EL_REGISTRO[tipoEvento]?.[campo] ?? TIPO_DE_CAMPO[campo] ?? 'texto';
}

/** Whether a field is said here, for the test that holds every field of the registry to it. */
export const tieneTipo = (campo: string): boolean => Object.prototype.hasOwnProperty.call(TIPO_DE_CAMPO, campo);

// ---------------------------------------------------------------------------------------------
// Records that name each other
// ---------------------------------------------------------------------------------------------

interface Registro {
  /** The field that names it, in the record that makes it and in those after it. */
  campo: string;
  crea: readonly string[];
  /** What can happen to it afterwards, each naming it by the same field. */
  despues: readonly string[];
}

const REGISTROS: readonly Registro[] = [
  { campo: 'ventaId', crea: ['venta.registrada'], despues: ['venta.anulada', 'devolucion.registrada', 'pago.confirmado', 'pago.rechazado'] },
  {
    campo: 'compraId',
    crea: ['compra.registrada', 'compra.saldo_inicial'],
    despues: ['compra.anulada', 'compra.costo_corregido', 'compra.cantidad_corregida', 'compra.forma_pago_corregida', 'compra.devuelta', 'compra.pagada', 'compra.pago_anulado', 'compra.saldo_inicial'],
  },
  { campo: 'gastoId', crea: ['gasto.registrado', 'faltante.explicado'], despues: ['gasto.anulado', 'gasto.pagado', 'gasto.pago_anulado'] },
  // A purchase's part paid as the goods came names its payment inside the purchase, and an
  // expense's part paid as it was recorded inside the expense (2026-10-16).
  {
    campo: 'pagoId',
    crea: ['compra.pagada', 'gasto.pagado', 'cotizaciones.pagadas', 'aguinaldo.pagado', 'compra.registrada', 'gasto.registrado'],
    despues: ['compra.pago_anulado', 'gasto.pago_anulado', 'aguinaldo.corregido'],
  },
  { campo: 'abonoId', crea: ['abono.registrado'], despues: ['abono.anulado', 'pago.confirmado', 'pago.rechazado'] },
  { campo: 'incobrableId', crea: ['incobrable.registrado'], despues: ['incobrable.anulado'] },
  { campo: 'depositoId', crea: ['efectivo.depositado', 'efectivo.sacado_del_banco'], despues: ['efectivo.deposito_anulado'] },
  { campo: 'transferenciaId', crea: ['banco.transferido'], despues: ['banco.transferencia_anulada'] },
  { campo: 'movimientoId', crea: ['dueno.retiro', 'dueno.aporte'], despues: ['dueno.movimiento_anulado'] },
  { campo: 'pedidoId', crea: ['pedido.tomado'], despues: ['pedido.actualizado', 'pedido.cancelado', 'venta.registrada'] },
  { campo: 'jornadaId', crea: ['jornada.abierta'], despues: ['jornada.liquidada'] },
  { campo: 'sesionId', crea: ['caja.abierta', 'jornada.abierta'], despues: ['caja.movimiento', 'caja.entregada', 'caja.cerrada', 'efectivo.recibido'] },
  { campo: 'planillaId', crea: ['planilla.corrida'], despues: ['planilla.pagada', 'planilla.corregida'] },
  { campo: 'recargaId', crea: ['recarga.registrada'], despues: ['pago.confirmado', 'pago.rechazado'] },
  { campo: 'productoId', crea: ['producto.creado'], despues: ['producto.actualizado', 'producto.desactivado', 'producto.reactivado', 'unidad_venta.creada'] },
  // The first presentation is made with its product, inside it.
  { campo: 'unidadVentaId', crea: ['unidad_venta.creada', 'producto.creado'], despues: ['unidad_venta.actualizada', 'unidad_venta.eliminada', 'precio.fijado'] },
  { campo: 'clienteId', crea: ['cliente.creado'], despues: ['cliente.actualizado', 'cliente.desactivado', 'cliente.saldo_inicial'] },
  { campo: 'proveedorId', crea: ['proveedor.creado'], despues: ['proveedor.actualizado', 'proveedor.desactivado'] },
  { campo: 'usuarioId', crea: ['usuario.creado'], despues: ['usuario.actualizado', 'usuario.pin_cambiado', 'usuario.desactivado', 'usuario.reactivado'] },
  { campo: 'empleadoId', crea: ['empleado.contratado'], despues: ['empleado.actualizado', 'empleado.adelanto', 'empleado.vacaciones', 'empleado.liquidado'] },
  { campo: 'dispositivoId', crea: ['dispositivo.registrado'], despues: ['dispositivo.revocado', 'dispositivo.reactivado'] },
  { campo: 'listaId', crea: ['lista_precio.creada'], despues: ['lista_precio.actualizada', 'lista_precio.eliminada'] },
  { campo: 'ofertaId', crea: ['oferta.creada'], despues: ['oferta.terminada'] },
];

const REGISTRO_DE = new Map(REGISTROS.map((r) => [r.campo, r]));

/** Fields that name a record by another field's ids. */
const NOMBRA_COMO: Record<string, string> = { relevoDe: 'sesionId', reemplazo: 'gastoId', origenProductoId: 'productoId' };

/** What has a name, by the field its records name it with. */
const ENTIDAD: Partial<Record<Tipo, string>> = {
  producto: 'productoId',
  cliente: 'clienteId',
  proveedor: 'proveedorId',
  empleado: 'empleadoId',
  equipo: 'dispositivoId',
  listaPrecio: 'listaId',
  presentacion: 'unidadVentaId',
};

/** The counts whose shortage the owner can explain afterwards (`faltante.explicado`). */
const CONTEOS = ['caja.abierta', 'caja.entregada', 'caja.cerrada', 'efectivo.recibido', 'efectivo.arqueo', 'jornada.liquidada'];

/** Where the books put cash a count found missing, or found over: a till's and the safe's. */
const FALTANTES = [ACCOUNTS.FALTANTE_CAJA.code, ACCOUNTS.FALTANTE_GENERAL.code];
const SOBRANTES = [ACCOUNTS.SOBRANTE_CAJA.code, ACCOUNTS.SOBRANTE_GENERAL.code];

/**
 * The kind of record a field of `tipoEvento` points at, or nothing: the field is no reference, or it
 * is the record's own id (a sale's `ventaId` names the sale it makes, not another).
 */
function registroQueNombra(tipoEvento: string, campo: string): Registro | 'evento' | null {
  const tipo = tipoDe(tipoEvento, campo);
  if (tipo === 'evento') return 'evento';
  if (tipo === 'usuario' && campo !== 'usuarioId') return null;
  const clave = tipo === 'registro' ? (NOMBRA_COMO[campo] ?? campo) : tipo === 'usuario' ? 'usuarioId' : ENTIDAD[tipo];
  const registro = clave ? REGISTRO_DE.get(clave) : undefined;
  if (!registro) return null;
  return clave === campo && registro.crea.includes(tipoEvento) ? null : registro;
}

const esPropio = (tipoEvento: string, campo: string) => REGISTRO_DE.get(campo)?.crea.includes(tipoEvento) ?? false;

/** What a change can be held against: the records before it about the same thing. */
const CAMBIOS: Record<string, { campo: string; desde: string[] }> = {
  'producto.actualizado': { campo: 'productoId', desde: ['producto.creado', 'producto.actualizado'] },
  'unidad_venta.actualizada': { campo: 'unidadVentaId', desde: ['unidad_venta.creada', 'unidad_venta.actualizada'] },
  'lista_precio.actualizada': { campo: 'listaId', desde: ['lista_precio.creada', 'lista_precio.actualizada'] },
  'proveedor.actualizado': { campo: 'proveedorId', desde: ['proveedor.creado', 'proveedor.actualizado'] },
  'cliente.actualizado': { campo: 'clienteId', desde: ['cliente.creado', 'cliente.actualizado'] },
  'usuario.actualizado': { campo: 'usuarioId', desde: ['usuario.creado', 'usuario.actualizado'] },
  'empleado.actualizado': { campo: 'empleadoId', desde: ['empleado.contratado', 'empleado.actualizado'] },
  'pedido.actualizado': { campo: 'pedidoId', desde: ['pedido.tomado', 'pedido.actualizado'] },
  // Sent again, these set the figure anew: what it said before is the one before.
  'cliente.saldo_inicial': { campo: 'clienteId', desde: ['cliente.saldo_inicial'] },
  'compra.saldo_inicial': { campo: 'compraId', desde: ['compra.saldo_inicial'] },
};

// ---------------------------------------------------------------------------------------------
// What the sheet draws
// ---------------------------------------------------------------------------------------------

export interface DatosDelRegistro {
  config: Settings;
  productos: ReadonlyMap<string, { nombre: string; nombreEn?: string | null; unidadBase: string }>;
  clientes: ReadonlyMap<string, string>;
  proveedores: ReadonlyMap<string, string>;
  usuarios: ReadonlyMap<string, { nombre: string; rol: Role }>;
  empleados: ReadonlyMap<string, string>;
  equipos: ReadonlyMap<string, { nombre: string; codigo: string }>;
  listas: ReadonlyMap<string, string>;
  presentaciones: ReadonlyMap<string, { nombre: string; productoId: string }>;
  /** The record each field naming another points at, by that field. */
  origenes: ReadonlyMap<string, LocalEvent>;
  /** What came after this record about the same thing, in the log's order. */
  despues: readonly LocalEvent[];
  /** What a change's fields said before it, as far as the log says. */
  antes: Readonly<Record<string, unknown>> | null;
  /** What the books made of it. */
  asientos: readonly AsientoRow[];
}

export interface Fila {
  campo: string;
  etiqueta: string;
  valor: string;
  /** What it said before this change, where it said something else. */
  antes?: string;
  /** The record it names, which opens from here. */
  abre?: string;
  /** Under the value: what that record is, when and by whom. */
  nota?: string;
  /** A photo's name: drawn rather than written. */
  foto?: string;
  /** Words rather than a figure: under the label, wrapping. */
  largo?: boolean;
}

export interface Linea {
  titulo: string;
  cantidad?: string;
  cifra?: string;
  detalles: string[];
}

export interface Bloque {
  campo: string;
  etiqueta: string;
  lineas: Linea[];
}

export interface Detalle {
  filas: Fila[];
  bloques: Bloque[];
}

// ---------------------------------------------------------------------------------------------
// Saying a value
// ---------------------------------------------------------------------------------------------

type Objeto = Record<string, unknown>;

const payloadDe = (e: { payload?: unknown } | undefined): Objeto =>
  e?.payload && typeof e.payload === 'object' ? (e.payload as Objeto) : {};

const enDolares = (c: number) => `US$ ${formatMoney(c as Centavos, { symbol: false })}`;
const dinero = (c: number, moneda: 'NIO' | 'USD') => (moneda === 'USD' ? enDolares(c) : formatMoney(c as Centavos));

/** A Nicaraguan date as a person says it: «12 oct 2026». */
const fecha = (f: string) =>
  new Date(`${f}T12:00:00Z`).toLocaleDateString(localeActual(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const mes = (m: string) =>
  new Date(`${m}-15T12:00:00Z`).toLocaleDateString(localeActual(), { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** A moment, in Nicaragua: «12 oct 2026, 2:41 p. m.». */
export const instante = (iso: string) =>
  new Date(iso).toLocaleString(localeActual(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ZONA_HORARIA,
  });

/** The moment a record was made, in full: the day of the week, and the second. */
export const cuandoLargo = (iso: string) =>
  new Date(iso).toLocaleString(localeActual(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: ZONA_HORARIA,
  });

/** How long, the way a person would say it: minutes, then hours, then days. */
export function duracion(ms: number): string {
  const minutos = Math.round(Math.abs(ms) / 60_000);
  if (minutos < 120) return t('registro.minutos', { n: minutos });
  const horas = Math.round(minutos / 60);
  if (horas < 48) return t('registro.horas', { n: horas });
  return t('registro.dias', { n: Math.round(horas / 24) });
}

interface Donde {
  tipoEvento: string;
  /** The object the field is in: the payload, or one of its lines. */
  objeto: Objeto;
  payload: Objeto;
  datos: DatosDelRegistro;
}

function nombreDe(tipo: Tipo, id: string, d: DatosDelRegistro): string | undefined {
  switch (tipo) {
    case 'producto': {
      const p = d.productos.get(id);
      return p && nombreProducto(p);
    }
    case 'cliente':
      return d.clientes.get(id);
    case 'proveedor':
      return d.proveedores.get(id);
    case 'usuario':
      return d.usuarios.get(id)?.nombre;
    case 'empleado':
      return d.empleados.get(id);
    case 'equipo': {
      const e = d.equipos.get(id);
      return e && `${e.nombre} (${e.codigo})`;
    }
    case 'listaPrecio':
      return d.listas.get(id);
    case 'presentacion': {
      const u = d.presentaciones.get(id);
      const p = u && d.productos.get(u.productoId);
      return u && (p ? `${nombreProducto(p)} · ${u.nombre}` : u.nombre);
    }
    default:
      return undefined;
  }
}

const unidadDeProducto = (id: unknown, d: DatosDelRegistro) => {
  const p = typeof id === 'string' ? d.productos.get(id) : undefined;
  return p ? etiquetaUnidad(p.unidadBase, p.unidadBase) : '';
};

function monedaDe(campo: string, w: Donde): 'NIO' | 'USD' {
  const p = w.payload;
  if (w.tipoEvento === 'banco.transferido') return esCuentaEnDolares(String(campo === 'recibido' ? p.hacia : p.desde)) ? 'USD' : 'NIO';
  // A corrected cost is in the purchase's currency, which the purchase says.
  const compra = w.tipoEvento === 'compra.costo_corregido' ? payloadDe(w.datos.origenes.get('compraId')) : p;
  return compra.moneda === 'USD' ? 'USD' : 'NIO';
}

/** The line of the sale or the purchase a field points at by its place in it. */
function lineaDelOrigen(w: Donde, indice: number): Objeto | undefined {
  const origen = w.datos.origenes.get('ventaId') ?? w.datos.origenes.get('compraId');
  const lineas = payloadDe(origen).lineas;
  const linea = Array.isArray(lineas) ? lineas[indice] : undefined;
  return linea && typeof linea === 'object' ? (linea as Objeto) : undefined;
}

/** A quantity's unit: the presentation sold, or the product's own. */
function unidadDeCantidad(campo: string, w: Donde): string {
  const o = w.objeto;
  const d = w.datos;
  if (typeof o.unidadVentaId === 'string') return d.presentaciones.get(o.unidadVentaId)?.nombre ?? '';
  if (typeof o.indice === 'number') {
    const linea = lineaDelOrigen(w, o.indice);
    if (!linea) return '';
    if (typeof linea.unidadVentaId === 'string') return d.presentaciones.get(linea.unidadVentaId)?.nombre ?? '';
    return unidadDeProducto(linea.productoId, d);
  }
  if (campo === 'origenCantidad' || campo === 'mermaCantidad') return unidadDeProducto(o.origenProductoId, d);
  // What one destination of a repack holds of the product opened, in that product's unit.
  if (campo === 'consumido') return unidadDeProducto(w.payload.origenProductoId, d);
  if (typeof o.productoId === 'string') return unidadDeProducto(o.productoId, d);
  if (typeof w.payload.unidadBase === 'string') return etiquetaUnidad(w.payload.unidadBase, w.payload.unidadBase);
  return unidadDeProducto(w.payload.productoId, d);
}

/** What a presentation holds is counted in its product's unit. */
function unidadDelFactor(w: Donde): string {
  const p = w.payload;
  if (typeof p.unidadBase === 'string') return etiquetaUnidad(p.unidadBase, p.unidadBase);
  if (typeof p.productoId === 'string') return unidadDeProducto(p.productoId, w.datos);
  const u = typeof p.unidadVentaId === 'string' ? w.datos.presentaciones.get(p.unidadVentaId) : undefined;
  return u ? unidadDeProducto(u.productoId, w.datos) : '';
}

function forma(campo: string, v: string, tipoEvento: string): string {
  const propia = etiquetaValor(campo, v);
  if (propia) return propia;
  // A sale's «credit» is fiado; a purchase's is the shop owing the supplier.
  if (v === 'credito') return t(tipoEvento === 'venta.registrada' ? 'pago.fiado' : 'pago.credito');
  return t(`pago.${v}` as Clave);
}

function eleccion(campo: string, v: string, tipoEvento: string): string {
  const propia = etiquetaValor(campo, v);
  if (propia) return propia;
  if (campo === 'tipo' && tipoEvento.startsWith('dispositivo.')) return t(`personal.tipo.${v}` as Clave);
  if (campo === 'tipo' && tipoEvento.startsWith('cliente.')) return t(v === 'mayorista' ? 'clientes.etiquetaMayorista' : 'clientes.etiquetaMostrador');
  if (campo === 'motivo' && tipoEvento === 'inventario.salida') return t(`salidaInv.${v}` as Clave);
  if (campo === 'motivo' && tipoEvento === 'empleado.liquidado') return t(`salida.${v}` as Clave);
  if (campo === 'motivo' && tipoEvento === 'oferta.creada') return t(`ofertas.motivo.${v}` as Clave);
  if (campo === 'frecuencia' && (FRECUENCIAS_PAGO as readonly string[]).includes(v)) return t(`planilla.frecuencia.${v}` as Clave);
  return v;
}

function lugar(v: string, config: Settings): string {
  const id = idDeCamion(v);
  const camion = id ? config.camiones.find((c) => c.id === id) : undefined;
  return camion ? `${etiquetaLugar(v)} ${camion.nombre}` : etiquetaLugar(v);
}

function cuenta(v: string, config: Settings): string {
  if (esCuentaDeBanco(v)) return nombreDeCuenta(config, v, t);
  const a = (ACCOUNTS as Record<string, { code: string; name: string } | undefined>)[v];
  return a ? etiquetaCuenta(a.code, a.name, config.cuentasBanco) : v;
}

const AJUSTES_EN_CORDOBAS = new Set<string>(['cajaRedondeoCentavos', 'cajaFondo', 'ventaRapidaMaxima', 'faltanteAviso', 'cajaReconteoDesde', 'metaVentasMes', 'metaUtilidadMes']);
const AJUSTES_EN_PORCENTAJE = new Set<string>(['precioMargenMinimoBp', 'ivaTasaBp', 'inssLaboralBp', 'inssPatronalBp', 'inatecBp']);

/** A setting's value as «Configuración» shows it: a rate, an amount, a percentage, minutes. */
function valorDeAjuste(clave: string, v: unknown, config: Settings): string {
  if (v === null || v === undefined) return t('registro.ninguno');
  if (typeof v === 'boolean') return t(v ? 'comun.si' : 'comun.no');
  if (typeof v === 'number') {
    if (clave === 'fxTasaTienda' || clave === 'fxTasaOficial') return formatFxRate(v as FxRate);
    if (AJUSTES_EN_CORDOBAS.has(clave)) return formatMoney(v as Centavos);
    if (AJUSTES_EN_PORCENTAJE.has(clave)) return verMargen(v);
    if (clave === 'bloqueoMinutos') return t('registro.minutos', { n: v });
    if (clave === 'equipoCalladoHoras') return t('registro.horas', { n: v });
    if (clave === 'vencimientoAlertaDias') return t('registro.dias', { n: v });
    return String(v);
  }
  if (typeof v === 'string') {
    if (clave === 'cuentaTransferencias' || clave === 'cuentaTarjeta') return cuenta(v, config);
    return v.trim() || t('registro.vacio');
  }
  if (Array.isArray(v)) {
    // Trucks, bank accounts, fixed expenses: each by its name.
    const nombres = v.map((x) => (x && typeof x === 'object' ? ((x as Objeto).nombre ?? (x as Objeto).descripcion) : undefined));
    if (nombres.every((n): n is string => typeof n === 'string')) return nombres.join(', ') || t('registro.ninguno');
    return t('registro.cosas', { n: v.length });
  }
  if (clave === 'presupuestosGastos') {
    // Each account by its name, with what it may spend a month.
    const filas = Object.entries(v as Record<string, number>).map(([codigo, monto]) => {
      const a = Object.values(ACCOUNTS).find((x) => x.code === codigo);
      return `${a ? etiquetaCuenta(a.code, a.name, config.cuentasBanco) : codigo} ${formatMoney(monto as Centavos)}`;
    });
    return filas.join(', ') || t('registro.ninguno');
  }
  return t('registro.cosas', { n: Object.keys(v as Objeto).length });
}

function valorDe(campo: string, v: unknown, w: Donde): string {
  if (v === null || v === undefined) return t('registro.ninguno');
  const tipo = tipoDe(w.tipoEvento, campo);
  const n = typeof v === 'number' ? v : Number.NaN;
  const d = w.datos;
  switch (tipo) {
    case 'texto':
      // A field this version does not know may hold anything.
      return typeof v === 'object' ? JSON.stringify(v) : String(v).trim() || t('registro.vacio');
    case 'numero':
      return String(v);
    case 'siNo':
      return t(v ? 'comun.si' : 'comun.no');
    case 'cordobas':
      return formatMoney(n as Centavos, { signed: campo === 'redondeo' });
    case 'dolares':
      return enDolares(n);
    case 'enSuMoneda':
      return dinero(n, monedaDe(campo, w));
    case 'costoUnitario':
      return t('registro.porUnidad', { costo: formatMoney(costOf(n as UnitCost, 1000 as Milli)), unidad: unidadDeCantidad(campo, w) || '—' });
    case 'tasa':
      return formatFxRate(n as FxRate);
    case 'cantidad':
      return `${formatQuantity(n as Milli)} ${unidadDeCantidad(campo, w)}`.trim();
    case 'factor':
      return `${formatQuantity(n as Milli)} ${unidadDelFactor(w)}`.trim();
    case 'dias':
      return t('registro.dias', { n: formatQuantity(n as Milli) });
    case 'tarifa': {
      // So much a day worked, a week or fifteen days, as the record says they are paid; a change
      // that leaves that be, as they were paid before it.
      const de = (k: string) => w.payload[k] ?? d.antes?.[k];
      const frecuencia = de('frecuencia');
      const por = de('porDia') ? 'dia' : frecuencia === 'semanal' || frecuencia === 'quincenal' ? frecuencia : 'mensual';
      return dichoAcordado(n as Centavos, por);
    }
    case 'fecha':
      return fecha(String(v));
    case 'mes':
      return mes(String(v));
    case 'instante':
      return instante(String(v));
    case 'diaSemana':
      return t(`dia.${n}` as Clave);
    case 'forma':
      return forma(campo, String(v), w.tipoEvento);
    case 'lugar':
      return lugar(String(v), d.config);
    case 'cuenta':
      return cuenta(String(v), d.config);
    case 'rol':
      return etiquetaRol(v as Role);
    case 'categoria':
      return w.tipoEvento.startsWith('producto.') ? etiquetaCategoriaProducto(String(v)) : etiquetaCategoriaGasto(v as CategoriaGasto);
    case 'unidad':
      return etiquetaUnidad(String(v), String(v));
    case 'moneda':
      return t(v === 'USD' ? 'gastos.dolares' : 'gastos.cordobas');
    case 'eleccion':
      return eleccion(campo, String(v), w.tipoEvento);
    case 'producto':
    case 'cliente':
    case 'proveedor':
    case 'usuario':
    case 'empleado':
    case 'equipo':
    case 'listaPrecio':
    case 'presentacion':
      return nombreDe(tipo, String(v), d) ?? t('registro.noEstaAqui');
    case 'registro':
    case 'evento': {
      const origen = w.objeto === w.payload ? d.origenes.get(campo) : undefined;
      return origen ? resumen(origen, d) || etiquetaEventoIdioma(origen.type, origen.payload) : t('registro.noEstaAqui');
    }
    case 'indice': {
      // A payment of the sale, for a transfer confirmed or rejected; a line, for the rest.
      if (w.tipoEvento.startsWith('pago.')) {
        const pagos = payloadDe(d.origenes.get('ventaId')).pagos;
        const pago = Array.isArray(pagos) ? (pagos[n] as Objeto | undefined) : undefined;
        return pago ? `${forma('forma', String(pago.forma), 'venta.registrada')} · ${formatMoney(Number(pago.monto) as Centavos)}` : t('registro.numeroN', { n: n + 1 });
      }
      const linea = lineaDelOrigen(w, n);
      if (!linea) return t('registro.numeroN', { n: n + 1 });
      const producto = typeof linea.productoId === 'string' ? nombreDe('producto', linea.productoId, d) : undefined;
      return producto ?? String(linea.descripcion ?? t('registro.numeroN', { n: n + 1 }));
    }
    case 'codigos':
      return Array.isArray(v) ? v.join(', ') || t('registro.ninguno') : String(v);
    case 'foto':
      return t('registro.conFoto');
    case 'gastoFijo': {
      const g = v as Objeto;
      const fijo = d.config.gastosFijos.find((x) => x.id === g.id);
      return [fijo?.descripcion, typeof g.periodo === 'string' ? mes(g.periodo) : undefined].filter(Boolean).join(' · ');
    }
    case 'objeto':
      return objeto(campo, v as Objeto, w);
    case 'ajuste':
      return campo === 'clave' ? (NOMBRE_AJUSTE[v as SettingKey] ? t(NOMBRE_AJUSTE[v as SettingKey]!) : String(v)) : valorDeAjuste(String(w.payload.clave), v, d.config);
    case 'lineas':
    case 'oculto':
      return '';
  }
}

/** What an object in `campo` holds, wherever in the record the field is: a purchase line's batch. */
function camposDentro(campos: readonly CampoDeEvento[], campo: string): readonly CampoDeEvento[] | undefined {
  for (const c of campos) {
    if (c.nombre === campo && c.campos) return c.campos;
    const hondo = c.campos && camposDentro(c.campos, campo);
    if (hondo) return hondo;
  }
  return undefined;
}

/** A batch, a first presentation: what it holds on one line. Its name first, bare. */
function objeto(campo: string, o: Objeto, w: Donde): string {
  const dentro: Donde = { ...w, objeto: o };
  const campos = camposDentro(camposDelEvento(w.tipoEvento), campo) ?? Object.keys(o).map((nombre) => ({ nombre, opcional: true }));
  return campos
    .filter((c) => o[c.nombre] !== undefined && !['oculto', 'registro', 'presentacion'].includes(tipoDe(w.tipoEvento, c.nombre)))
    .map((c) => {
      const valor = valorDe(c.nombre, o[c.nombre], dentro);
      return c.nombre === 'nombre' || c.nombre === 'numero' ? valor : `${etiquetaCampo(c.nombre, w.tipoEvento)}: ${valor}`;
    })
    .join(' · ');
}

// ---------------------------------------------------------------------------------------------
// One line of what another record is
// ---------------------------------------------------------------------------------------------

/**
 * What a record is, in a few words: a sale's ticket and total, a payment's client and amount, a
 * product's name. For the row that names it from another record, and for what came after.
 */
export function resumen(e: LocalEvent, d: DatosDelRegistro): string {
  const p = payloadDe(e);
  const w: Donde = { tipoEvento: e.type, objeto: p, payload: p, datos: d };
  const de = (campo: string) => (p[campo] === undefined || p[campo] === null ? undefined : valorDe(campo, p[campo], w));
  const partes = ((): (string | undefined)[] => {
    switch (e.type) {
      case 'venta.registrada':
        return [de('ticket'), de('total')];
      case 'compra.registrada': {
        const lineas = Array.isArray(p.lineas) ? (p.lineas as Objeto[]) : [];
        const total = lineas.reduce((s, l) => s + Number(l.costoTotal ?? 0), Number(p.flete ?? 0) + Number(p.iva ?? 0));
        return [de('proveedorId'), de('documento'), dinero(total, p.moneda === 'USD' ? 'USD' : 'NIO')];
      }
      case 'compra.saldo_inicial':
        return [de('proveedorId'), de('documento'), de('monto')];
      case 'gasto.registrado':
      case 'faltante.explicado':
        return [de('descripcion'), de('monto')];
      case 'abono.registrado':
      case 'incobrable.registrado':
      case 'cliente.saldo_inicial':
        return [de('clienteId'), de('monto')];
      case 'efectivo.depositado':
      case 'efectivo.sacado_del_banco':
        return [p.cordobas ? de('cordobas') : undefined, p.dolares ? de('dolares') : undefined];
      case 'banco.transferido':
        return [`${de('desde')} → ${de('hacia')}`, de('monto')];
      case 'pedido.tomado':
      case 'pedido.actualizado':
        // What the customer pays: the goods (`total`), and the delivery beside them since 2026-10-12.
        return [de('clienteId'), dinero(Number(p.total ?? 0) + Number(p.envio ?? 0), 'NIO')];
      case 'jornada.abierta':
        return [de('repartidorId'), de('camion')];
      case 'caja.abierta':
        return [d.usuarios.get(e.actorId)?.nombre, de('ubicacion')];
      case 'caja.movimiento':
        return [de('tipo'), de('monto'), de('motivo')];
      case 'caja.cerrada':
      case 'caja.entregada':
        return [de('conteoCordobas'), p.conteoDolares ? de('conteoDolares') : undefined];
      case 'caja.apertura_corregida':
        return [de('fondoInicial'), p.fondoDolares ? de('fondoDolares') : undefined, de('motivo')];
      case 'caja.corte_corregido':
        return [de('conteoCordobas'), p.conteoDolares ? de('conteoDolares') : undefined, de('motivo')];
      case 'efectivo.recibido':
        return [de('contadoCordobas'), p.contadoDolares ? de('contadoDolares') : undefined];
      case 'planilla.corrida':
        return p.desde && p.hasta ? [`${de('desde')} – ${de('hasta')}`] : [de('periodo')];
      case 'planilla.corregida':
        // How many it pays now, or that it was voided; and why, when said.
        return [Array.isArray(p.lineas) && p.lineas.length > 0 ? t('registro.personasN', { n: p.lineas.length }) : t('registro.planillaAnulada'), de('nota')];
      case 'aguinaldo.pagado':
      case 'aguinaldo.corregido': {
        // To how many, and what it comes to; voided, that it was; and why, when said.
        const lineas = Array.isArray(p.lineas) ? (p.lineas as Objeto[]) : [];
        if (lineas.length === 0) return [t('registro.pagoAnulado'), de('nota')];
        return [t('registro.personasN', { n: lineas.length }), dinero(lineas.reduce((a, l) => a + Number(l.monto ?? 0), 0), 'NIO'), de('nota')];
      }
      case 'recarga.registrada':
        return [de('operadora'), de('telefono'), de('montoFacial')];
      case 'precio.fijado':
        return [de('listaId'), de('precio')];
      case 'oferta.creada':
        // What, from when to when, and why.
        return [de('productoId'), `${de('desde')} – ${de('hasta')}`, de('motivo')];
      case 'oferta.terminada':
        return [de('fecha'), de('nota')];
      case 'compra.costo_corregido':
        return [de('costoTotal'), de('motivo')];
      case 'compra.cantidad_corregida':
        return [de('indice'), de('cantidadBase'), de('motivo')];
      case 'compra.forma_pago_corregida':
        return [de('formaPago'), de('motivo')];
      case 'inventario.costo_corregido':
        return [de('productoId'), de('costoUnitario'), de('motivo')];
      case 'pago.confirmado':
        return [de('referencia')];
      default: {
        // A change says what it changed; the rest, its name, what it was for, its amount.
        if (/\.actualizad[oa]$/.test(e.type)) {
          const cambiados = Object.keys(p).filter((k) => !esPropio(e.type, k) && !registroQueNombra(e.type, k) && tipoDe(e.type, k) !== 'oculto');
          return [cambiados.map((k) => etiquetaCampo(k, e.type)).join(', ')];
        }
        const dicho = ['nombre', 'descripcion', 'motivo', 'referencia'].map(de).find(Boolean);
        const cifra = ['total', 'monto', 'conteoEfectivo', 'contadoCordobas'].map(de).find(Boolean);
        return [dicho, cifra];
      }
    }
  })();
  return partes.filter((x): x is string => Boolean(x)).join(' · ');
}

// ---------------------------------------------------------------------------------------------
// The record, said
// ---------------------------------------------------------------------------------------------

const ES_CAMBIO = /\.actualizad[oa]$/;

const vacio = (v: unknown) => v === null || v === false || v === 0 || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * Whether a field says nothing worth a row: absent; or, left to its default, nothing, zero or
 * `false` — a sale with no discount does not need a row that says so — and the usual case of a
 * flag, a payment confirmed, córdobas. A flag everybody has on is the other way round: only off is
 * worth a row. A change says everything it holds: there, a `null` is something taken away, and a
 * zero something set to zero.
 */
function callado(c: CampoDeEvento, v: unknown, cambio: boolean): boolean {
  if (v === undefined) return true;
  if (cambio || !c.opcional) return false;
  // A flag left on for everybody says something off: «Paga INSS: No».
  if (typeof v === 'boolean' && typeof c.porDefecto === 'boolean') return v === c.porDefecto;
  if (vacio(v)) return true;
  return (typeof v === 'boolean' || c.nombre === 'estado' || c.nombre === 'moneda') && c.porDefecto === v;
}

const igual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const LARGO = 28;

export function describirRegistro(e: LocalEvent, d: DatosDelRegistro): Detalle {
  const payload = payloadDe(e);
  const cambio = ES_CAMBIO.test(e.type);
  const conocidos = camposDelEvento(e.type);
  // A field the registry does not know — from a newer version — is shown all the same.
  const campos: CampoDeEvento[] = [
    ...conocidos,
    ...Object.keys(payload)
      .filter((k) => !conocidos.some((c) => c.nombre === k))
      .map((nombre) => ({ nombre, opcional: true })),
  ];
  const w: Donde = { tipoEvento: e.type, objeto: payload, payload, datos: d };
  const filas: Fila[] = [];
  const bloques: Bloque[] = [];

  for (const c of campos) {
    const v = payload[c.nombre];
    if (callado(c, v, cambio)) continue;
    const tipo = tipoDe(e.type, c.nombre);
    if (tipo === 'oculto' || esPropio(e.type, c.nombre)) continue;
    // The setting changed is in the record's title already.
    if (e.type === 'config.cambiada' && c.nombre === 'clave' && NOMBRE_AJUSTE[v as SettingKey]) continue;
    if (c.campos && c.lista && Array.isArray(v)) {
      bloques.push({ campo: c.nombre, etiqueta: etiquetaCampo(c.nombre, e.type), lineas: v.map((x) => linea(c.campos!, x, w)) });
      continue;
    }

    // Paid by the day, the week or fifteen days, the monthly salary is only what that comes to.
    const etiqueta = c.nombre === 'salarioMensual' && typeof payload.tarifa === 'number' ? t('registro.equivaleAlMes') : etiquetaCampo(c.nombre, e.type);
    const fila: Fila = { campo: c.nombre, etiqueta, valor: valorDe(c.nombre, v, w) };
    if (tipo === 'foto' && typeof v === 'string') fila.foto = v;
    if ((tipo === 'texto' || tipo === 'objeto' || tipo === 'codigos') && fila.valor.length > LARGO) fila.largo = true;
    const origen = d.origenes.get(c.nombre);
    if (origen) {
      fila.abre = origen.id;
      fila.nota = [etiquetaEventoIdioma(origen.type, origen.payload), instante(origen.clientTs), d.usuarios.get(origen.actorId)?.nombre]
        .filter(Boolean)
        .join(' · ');
    }
    const antes = d.antes;
    if (antes && Object.prototype.hasOwnProperty.call(antes, c.nombre) && !igual(antes[c.nombre], v)) {
      // An agreed pay before, by what it was agreed for before: a week's, before it became a day's.
      fila.antes = valorDe(c.nombre, antes[c.nombre], tipo === 'tarifa' ? { ...w, objeto: antes, payload: antes } : w);
    }
    filas.push(fila);
  }

  // What a count is opened for, which the count itself does not hold: the books worked out what
  // was expected, and put what was short or over on accounts of its own.
  const lineasDeLibro = d.asientos.flatMap((a) => a.lineas);
  if (CONTEOS.includes(e.type) && lineasDeLibro.length > 0) {
    const neto = (cuentas: string[], signo: 1 | -1) =>
      lineasDeLibro.filter((l) => cuentas.includes(l.cuenta)).reduce((s, l) => s + signo * (l.debe - l.haber), 0);
    const falto = neto(FALTANTES, 1);
    const sobro = neto(SOBRANTES, -1);
    if (falto > 0) filas.push({ campo: 'falto', etiqueta: t('registro.falto'), valor: formatMoney(falto as Centavos) });
    if (sobro > 0) filas.push({ campo: 'sobro', etiqueta: t('registro.sobro'), valor: formatMoney(sobro as Centavos) });
    if (falto <= 0 && sobro <= 0) filas.push({ campo: 'cuadro', etiqueta: t('registro.diferencia'), valor: t('registro.cuadro') });
  }
  // What it found once the owner corrected it (`conteosCorregidos`): the count above stays as typed.
  const corregido = d.config.conteosCorregidos[e.id];
  if (corregido !== undefined) {
    const monto = formatMoney(Math.abs(corregido) as Centavos);
    filas.push({
      campo: 'corregido',
      etiqueta: t('registro.corregido'),
      valor: corregido < 0 ? t('registro.corregidoFalta', { monto }) : corregido > 0 ? t('registro.corregidoSobra', { monto }) : t('registro.cuadro'),
    });
  }
  return { filas, bloques };
}

/**
 * One line of a list — a product sold, a payment, an employee's pay — as the sale's own sheet shows
 * one: what it is, how much of it, its amount, and the rest small under it.
 */
function linea(campos: readonly CampoDeEvento[], x: unknown, w: Donde): Linea {
  const o = (x && typeof x === 'object' ? x : {}) as Objeto;
  const aqui: Donde = { ...w, objeto: o };
  // Said by the line's title and figures, or the books' own: the price's list snapshot, the cost.
  const dichos = new Set(['factor', 'costoReferencia', 'pagoId', 'unidadVentaId']);

  let titulo = '';
  if ('productoId' in o) {
    dichos.add('productoId').add('descripcion');
    titulo =
      typeof o.productoId === 'string'
        ? (nombreDe('producto', o.productoId, w.datos) ?? String(o.descripcion ?? t('registro.noEstaAqui')))
        : `${String(o.descripcion ?? '')} · ${t('registro.ventaRapida')}`;
  } else {
    const k = ['indice', 'empleadoId', 'forma', 'formaPago', 'listaId'].find((c) => o[c] !== undefined && o[c] !== null);
    if (k) {
      dichos.add(k);
      titulo = valorDe(k, o[k], aqui);
    }
  }

  let cantidad: string | undefined;
  const kc = ['cantidad', 'cantidadBase', 'cantidadContada'].find((c) => typeof o[c] === 'number');
  if (kc) {
    dichos.add(kc);
    cantidad = valorDe(kc, o[kc], aqui);
  }
  if (typeof o.precioUnitario === 'number') {
    dichos.add('precioUnitario');
    cantidad = `${cantidad ?? ''} × ${formatMoney(o.precioUnitario as Centavos, { symbol: false })}`.trim();
  }
  let cifra: string | undefined;
  const kf = ['subtotal', 'costoTotal', 'monto', 'precio', 'bruto'].find((c) => typeof o[c] === 'number');
  if (kf) {
    dichos.add(kf);
    cifra = valorDe(kf, o[kf], aqui);
  }

  const detalles: string[] = [];
  for (const c of campos) {
    const v = o[c.nombre];
    if (dichos.has(c.nombre) || callado(c, v, false)) continue;
    const tipo = tipoDe(w.tipoEvento, c.nombre);
    if (tipo === 'oculto') continue;
    // The list's price, when it is what was charged, says nothing the price did not.
    if (c.nombre === 'precioLista' && v === o.precioUnitario) continue;
    if (c.nombre === 'sesionId') {
      detalles.push(t('registro.deLaGaveta'));
      continue;
    }
    if (tipo === 'registro') continue;
    detalles.push(`${etiquetaCampo(c.nombre, w.tipoEvento)}: ${valorDe(c.nombre, v, aqui)}`);
  }
  // A count says how far off the books were.
  if (typeof o.cantidadContada === 'number' && typeof o.cantidadSistema === 'number' && o.cantidadContada !== o.cantidadSistema) {
    const diferencia = o.cantidadContada - o.cantidadSistema;
    const unidad = unidadDeCantidad('cantidadContada', aqui);
    detalles.push(`${t('registro.diferencia')}: ${diferencia > 0 ? '+' : ''}${formatQuantity(diferencia as Milli)} ${unidad}`.trim());
  }

  return { titulo, ...(cantidad ? { cantidad } : {}), ...(cifra ? { cifra } : {}), detalles };
}

// ---------------------------------------------------------------------------------------------
// Reading it from this device
// ---------------------------------------------------------------------------------------------

export interface RegistroAbierto {
  evento: LocalEvent;
  datos: DatosDelRegistro;
}

/** The id of the presentation a product was made with, inside its `producto.creado`. */
const unidadDelProducto = (e: LocalEvent) => (payloadDe(e).unidadVenta as Objeto | undefined)?.unidadVentaId;

const nombraA = (e: LocalEvent, campo: string, id: string): boolean => {
  const p = payloadDe(e);
  if (p[campo] === id) return true;
  if (campo === 'unidadVentaId' && e.type === 'producto.creado') return unidadDelProducto(e) === id;
  if (campo === 'pagoId' && (e.type === 'compra.registrada' || e.type === 'gasto.registrado')) {
    return Array.isArray(p.pagos) && p.pagos.some((x) => (x as Objeto).pagoId === id);
  }
  return false;
};

/** The record that made `id`, newest first: a sale held twice (its backup copy) is the same sale. */
async function creacionDe(r: Registro, id: string): Promise<LocalEvent | undefined> {
  return db.eventos
    .where('type')
    .anyOf([...r.crea])
    .reverse()
    .filter((x) => x.estado !== 'rechazado' && nombraA(x, r.campo, id))
    .first();
}

async function buscarOrigenes(e: LocalEvent): Promise<Map<string, LocalEvent>> {
  const origenes = new Map<string, LocalEvent>();
  await Promise.all(
    Object.entries(payloadDe(e)).map(async ([campo, v]) => {
      if (typeof v !== 'string') return;
      const r = registroQueNombra(e.type, campo);
      if (!r) return;
      const origen = r === 'evento' ? await db.eventos.get(v) : await creacionDe(r, v);
      if (origen && origen.id !== e.id) origenes.set(campo, origen);
    }),
  );
  return origenes;
}

async function buscarDespues(e: LocalEvent): Promise<LocalEvent[]> {
  const p = payloadDe(e);
  const busquedas: Promise<LocalEvent[]>[] = [];
  for (const r of REGISTROS) {
    if (!r.crea.includes(e.type)) continue;
    const id = r.campo === 'unidadVentaId' && e.type === 'producto.creado' ? unidadDelProducto(e) : p[r.campo];
    if (typeof id !== 'string') continue;
    busquedas.push(
      db.eventos
        .where('type')
        .anyOf([...r.despues])
        .filter((x) => x.id !== e.id && payloadDe(x)[r.campo] === id)
        .toArray(),
    );
  }
  if (CONTEOS.includes(e.type)) {
    busquedas.push(db.eventos.where('type').equals('faltante.explicado').filter((x) => payloadDe(x).eventoId === e.id).toArray());
  }
  // A till's count corrected: an opening's corrections name its corte too, once it has closed, and
  // a corte's the opening that took the drawer over, once one has.
  if (e.type === 'caja.abierta' || e.type === 'caja.cerrada') {
    busquedas.push(
      db.eventos
        .where('type')
        .anyOf(['caja.apertura_corregida', 'caja.corte_corregido'])
        .filter((x) => [payloadDe(x).eventoId, payloadDe(x).corteId, payloadDe(x).aperturaSiguienteId].includes(e.id))
        .toArray(),
    );
  }
  // A run's payment, and the corrections that changed it by its id (2026-10-31).
  if (e.type === 'planilla.pagada') {
    busquedas.push(db.eventos.where('type').equals('planilla.corregida').filter((x) => payloadDe(x).pagoId === e.id).toArray());
  }
  const todos = new Map((await Promise.all(busquedas)).flat().map((x) => [x.id, x]));
  return [...todos.values()].filter((x) => ordenReplay(x, e) > 0).sort(ordenReplay);
}

/** The records before `e` in the log, of these types, that are about the same thing. */
async function anteriores(e: LocalEvent, tipos: string[], de: (x: LocalEvent) => boolean): Promise<LocalEvent[]> {
  const previos = await db.eventos
    .where('type')
    .anyOf(tipos)
    .filter((x) => x.id !== e.id && x.estado !== 'rechazado' && ordenReplay(x, e) < 0 && de(x))
    .toArray();
  return previos.sort(ordenReplay);
}

/** What a change's fields said before it: each the last any record before it set. */
async function buscarAntes(e: LocalEvent): Promise<Record<string, unknown> | null> {
  const p = payloadDe(e);
  if (e.type === 'config.cambiada') return Object.prototype.hasOwnProperty.call(p, 'anterior') ? { valor: p.anterior } : null;

  if (e.type === 'precio.fijado') {
    const mismo = (x: LocalEvent) => payloadDe(x).unidadVentaId === p.unidadVentaId && payloadDe(x).listaId === p.listaId;
    const previo = (await anteriores(e, ['precio.fijado'], mismo)).at(-1);
    if (previo) return { precio: payloadDe(previo).precio };
    // The price it was made with, on the product's first presentation.
    const creado = (await anteriores(e, ['producto.creado'], (x) => unidadDelProducto(x) === p.unidadVentaId)).at(-1);
    const precios = payloadDe(creado).precios;
    const precio = Array.isArray(precios) ? (precios as Objeto[]).find((x) => x.listaId === p.listaId)?.precio : undefined;
    return precio === undefined ? null : { precio };
  }

  // A line's cost or quantity, as the purchase said it or the last correction of that line left it.
  if (e.type === 'compra.costo_corregido' || e.type === 'compra.cantidad_corregida') {
    const campo = e.type === 'compra.costo_corregido' ? 'costoTotal' : 'cantidadBase';
    const previos = await anteriores(e, ['compra.registrada', e.type], (x) => {
      const q = payloadDe(x);
      return q.compraId === p.compraId && (x.type === 'compra.registrada' || q.indice === p.indice);
    });
    let antes: unknown;
    for (const x of previos) {
      const q = payloadDe(x);
      antes = x.type === 'compra.registrada' ? (Array.isArray(q.lineas) ? (q.lineas[Number(p.indice)] as Objeto | undefined)?.[campo] : undefined) : q[campo];
    }
    return antes === undefined ? null : { [campo]: antes };
  }

  // A till's count, as it was typed or the last correction of it the books took left it.
  if (e.type === 'caja.apertura_corregida' || e.type === 'caja.corte_corregido') {
    const sinEfecto = await leerSinEfecto();
    const previo = (await anteriores(e, [e.type], (x) => payloadDe(x).eventoId === p.eventoId)).filter((x) => !sinEfecto.has(x.id)).at(-1);
    const fuente = previo ?? (await db.eventos.get(String(p.eventoId)));
    if (!fuente) return null;
    const q = payloadDe(fuente);
    const campos = e.type === 'caja.apertura_corregida' ? ['fondoInicial', 'fondoDolares'] : ['conteoCordobas', 'conteoDolares', 'queda'];
    // Dollars nobody counted are none: «Antes: US$ 0.00» beside dollars counted now.
    return Object.fromEntries(campos.map((k) => [k, q[k] ?? 0]));
  }

  // How it was paid, as the purchase said it or the last correction of it left it.
  if (e.type === 'compra.forma_pago_corregida') {
    const previo = (await anteriores(e, ['compra.registrada', e.type], (x) => payloadDe(x).compraId === p.compraId)).at(-1);
    if (!previo) return null;
    const q = payloadDe(previo);
    return Object.fromEntries(['formaPago', 'sesionId', 'cuenta', 'vencePago'].filter((k) => q[k] !== undefined).map((k) => [k, q[k]]));
  }

  const cambio = CAMBIOS[e.type];
  const id = cambio ? p[cambio.campo] : undefined;
  if (!cambio || typeof id !== 'string') return null;
  const estado: Record<string, unknown> = {};
  if (e.type === 'unidad_venta.actualizada') {
    // The first presentation was made inside its product.
    const creado = (await anteriores(e, ['producto.creado'], (x) => unidadDelProducto(x) === id)).at(-1);
    const uv = payloadDe(creado).unidadVenta as Objeto | undefined;
    if (uv) Object.assign(estado, { nombre: uv.nombre, factor: uv.factor });
  }
  for (const x of await anteriores(e, cambio.desde, (x) => payloadDe(x)[cambio.campo] === id)) {
    for (const [k, v] of Object.entries(payloadDe(x))) if (v !== undefined) estado[k] = v;
  }
  return Object.keys(estado).length > 0 ? estado : null;
}

type Clase = 'producto' | 'cliente' | 'proveedor' | 'usuario' | 'empleado' | 'equipo' | 'listaPrecio' | 'presentacion';
const CLASES: readonly Clase[] = ['producto', 'cliente', 'proveedor', 'usuario', 'empleado', 'equipo', 'listaPrecio', 'presentacion'];

/** The names behind every id these records hold, and their actors' and devices'. */
async function cargarNombres(registros: readonly { type: string; payload?: unknown; actorId?: string; deviceId?: string }[]) {
  const ids = Object.fromEntries(CLASES.map((c) => [c, new Set<string>()])) as Record<Clase, Set<string>>;
  const recoger = (tipoEvento: string, o: Objeto) => {
    for (const [campo, v] of Object.entries(o)) {
      if (typeof v === 'string') {
        const tipo = tipoDe(tipoEvento, campo);
        if ((CLASES as readonly string[]).includes(tipo)) ids[tipo as Clase].add(v);
      } else if (Array.isArray(v)) {
        for (const x of v) if (x && typeof x === 'object') recoger(tipoEvento, x as Objeto);
      } else if (v && typeof v === 'object') {
        recoger(tipoEvento, v as Objeto);
      }
    }
  };
  for (const r of registros) {
    recoger(r.type, payloadDe(r));
    if (r.actorId) ids.usuario.add(r.actorId);
    if (r.deviceId) ids.equipo.add(r.deviceId);
  }

  const presentaciones = (await db.unidades.bulkGet([...ids.presentacion])).filter((u) => u !== undefined);
  for (const u of presentaciones) ids.producto.add(u.productoId);
  const [productos, clientes, proveedores, usuarios, empleados, equipos, listas] = await Promise.all([
    db.productos.bulkGet([...ids.producto]),
    db.clientes.bulkGet([...ids.cliente]),
    db.proveedores.bulkGet([...ids.proveedor]),
    db.usuarios.bulkGet([...ids.usuario]),
    db.empleados.bulkGet([...ids.empleado]),
    db.dispositivos.bulkGet([...ids.equipo]),
    db.listas.bulkGet([...ids.listaPrecio]),
  ]);
  const hay = <T>(x: T | undefined): x is T => x !== undefined;
  return {
    productos: new Map(productos.filter(hay).map((p) => [p.id, { nombre: p.nombre, nombreEn: p.nombreEn ?? null, unidadBase: p.unidadBase }])),
    clientes: new Map(clientes.filter(hay).map((c) => [c.id, c.nombre])),
    proveedores: new Map(proveedores.filter(hay).map((p) => [p.id, p.nombre])),
    usuarios: new Map(usuarios.filter(hay).map((u) => [u.id, { nombre: u.nombre, rol: u.rol }])),
    empleados: new Map(empleados.filter(hay).map((x) => [x.id, x.nombre])),
    equipos: new Map(equipos.filter(hay).map((x) => [x.id, { nombre: x.nombre, codigo: x.codigo }])),
    listas: new Map(listas.filter(hay).map((l) => [l.id, l.nombre])),
    presentaciones: new Map(presentaciones.map((u) => [u.id, { nombre: u.nombre, productoId: u.productoId }])),
  };
}

/** One record and everything its sheet says, from this device's copy. Null if it is not here. */
export async function cargarDetalle(id: string): Promise<RegistroAbierto | null> {
  const evento = await db.eventos.get(id);
  if (!evento) return null;
  const [origenes, despues, antes, asientos, config] = await Promise.all([
    buscarOrigenes(evento),
    buscarDespues(evento),
    buscarAntes(evento),
    db.asientos.where('eventId').equals(id).toArray(),
    readSettings(),
  ]);
  const nombres = await cargarNombres([
    evento,
    ...origenes.values(),
    ...despues,
    // What a change replaced names things too: the price list a client was on before.
    ...(antes ? [{ type: evento.type, payload: antes }] : []),
  ]);
  return { evento, datos: { config, ...nombres, origenes, despues, antes, asientos } };
}
